import React, { createContext, useContext, useState, useEffect } from 'react'
import { auth, getAccessToken, setAccessToken } from '../lib/api-client'

interface User {
  id: string
  email: string
  displayName: string
  role: 'admin' | 'manager' | 'user' | 'viewer'
}

interface AuthContextType {
  user: User | null
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  isLoading: boolean
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(false) // Start with false - no auth check on mount

  // Note: Not checking auth on mount to prevent blank page issues
  // User must explicitly log in
  useEffect(() => {
    console.log('AuthProvider mounted - no auto-login')
  }, [])

  const login = async (email: string, password: string) => {
    try {
      console.log('Attempting login for:', email)
      const response = await auth.login(email, password)
      console.log('Login response:', response)

      if (response.success && response.accessToken && response.user) {
        console.log('Login successful, setting token and user')
        setAccessToken(response.accessToken)
        setUser(response.user)
        localStorage.setItem('user', JSON.stringify(response.user))
      } else {
        throw new Error(response.error || 'Login failed')
      }
    } catch (error: any) {
      console.error('Login failed:', error)
      throw new Error(error.response?.data?.error || error.message || 'Login failed')
    }
  }

  const logout = async () => {
    try {
      console.log('Logging out...')
      await auth.logout()
    } catch (error) {
      console.error('Logout failed:', error)
    } finally {
      setUser(null)
      setAccessToken(null)
      localStorage.removeItem('user')
      console.log('Logged out successfully')
    }
  }

  return (
    <AuthContext.Provider value={{
      user,
      login,
      logout,
      isLoading,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
